// Vendor compliance documents — upload + row insert + scan kickoff.
//
// Two upload paths:
//   * Vendor admin from /vendor/settings → uploadAsVendor()
//   * Staff from VendorDetail (rare, on-the-vendor's-behalf) → uploadAsStaff()
//
// Both share the path convention {workspace_id}/{vendor_id}/{doc_type}/{uuid}.{ext}
// — the storage RLS policies enforce the workspace + vendor folder
// prefixes against the caller's session role.
//
// Reads are direct SELECT on vendor_documents (RLS does the scoping).
// Signed URLs for "View document" use the standard storage createSignedUrl
// against the same bucket.

import { supabase } from '@/lib/supabase'

const BUCKET = 'vendor-compliance-docs'
const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'] as const

export type DocType = 'w9' | 'cgl' | 'commercial_auto' | 'workers_comp'

function extFromMime(mime: string): string | null {
  switch (mime) {
    case 'application/pdf': return 'pdf'
    case 'image/jpeg':      return 'jpg'
    case 'image/png':       return 'png'
    case 'image/webp':      return 'webp'
    default: return null
  }
}

interface UploadCommon {
  workspaceId: string
  vendorId: string
  docType: DocType
  file: File
}

interface UploadAsVendorInput extends UploadCommon { vendorUserId: string }
interface UploadAsStaffInput  extends UploadCommon { staffProfileId: string }

export interface UploadResult {
  data: { id: string; storage_path: string } | null
  error: Error | null
}

async function performUpload(opts: UploadCommon & { uploadedByVendor?: string; uploadedByStaff?: string }): Promise<UploadResult> {
  if (!ALLOWED_MIME.includes(opts.file.type as typeof ALLOWED_MIME[number])) {
    return { data: null, error: new Error(`Unsupported file type: ${opts.file.type || 'unknown'}`) }
  }

  const ext = (opts.file.name.split('.').pop() || extFromMime(opts.file.type) || 'bin').toLowerCase()
  const objectId = crypto.randomUUID()
  const path = `${opts.workspaceId}/${opts.vendorId}/${opts.docType}/${objectId}.${ext}`

  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, opts.file, {
    cacheControl: '300',
    contentType: opts.file.type || undefined,
  })
  if (upErr) return { data: null, error: new Error(`Upload failed: ${upErr.message}`) }

  const { data: row, error: dbErr } = await supabase
    .from('vendor_documents')
    .insert({
      workspace_id: opts.workspaceId,
      vendor_id: opts.vendorId,
      doc_type: opts.docType,
      storage_path: path,
      file_name: opts.file.name,
      byte_size: opts.file.size,
      uploaded_by_vendor_user_id: opts.uploadedByVendor ?? null,
      uploaded_by:                opts.uploadedByStaff  ?? null,
    })
    .select('id, storage_path')
    .single()

  if (dbErr || !row) {
    // Best-effort orphan cleanup so storage doesn't drift from metadata.
    await supabase.storage.from(BUCKET).remove([path]).catch(() => { /* swallow */ })
    return { data: null, error: new Error(dbErr?.message ?? 'Row insert failed') }
  }
  return { data: row as { id: string; storage_path: string }, error: null }
}

export async function uploadVendorComplianceDoc(input: UploadAsVendorInput): Promise<UploadResult> {
  return performUpload({ ...input, uploadedByVendor: input.vendorUserId })
}

export async function uploadStaffComplianceDoc(input: UploadAsStaffInput): Promise<UploadResult> {
  return performUpload({ ...input, uploadedByStaff: input.staffProfileId })
}

export async function invokeDocumentScan(documentId: string): Promise<{ error: string | null }> {
  const { error } = await supabase.functions.invoke('vendor-document-scan', {
    body: { document_id: documentId },
  })
  return { error: error?.message ?? null }
}

export async function getComplianceDocSignedUrl(storagePath: string, ttlSeconds = 60): Promise<{ url: string | null; error: Error | null }> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(storagePath, ttlSeconds)
  if (error || !data?.signedUrl) return { url: null, error: error ?? new Error('No signed URL returned') }
  return { url: data.signedUrl, error: null }
}

// ─── Shared display helpers ──────────────────────────────────────────
export const DOC_TYPE_LABEL: Record<DocType, string> = {
  w9: 'W-9',
  cgl: 'Commercial General Liability',
  commercial_auto: 'Commercial Auto',
  workers_comp: "Workers' Compensation",
}

export const DOC_TYPE_SHORT: Record<DocType, string> = {
  w9: 'W-9',
  cgl: 'CGL',
  commercial_auto: 'Auto',
  workers_comp: 'WC',
}

// Computes a compliance "state" for a confirmed doc — drives the chip.
// Pending review and rejected don't have expiry semantics.
export type ComplianceState = 'valid' | 'expiring_soon' | 'expired' | 'pending_review' | 'rejected' | 'missing'

export function deriveDocState(opts: {
  hasRow: boolean
  status: 'pending_review' | 'confirmed' | 'rejected' | null
  expirationDate: string | null
  isRequired: boolean
}): ComplianceState {
  if (!opts.hasRow) return opts.isRequired ? 'missing' : 'missing'
  if (opts.status === 'pending_review') return 'pending_review'
  if (opts.status === 'rejected') return 'rejected'
  // confirmed
  if (!opts.expirationDate) return 'valid' // confirmed but no expiry (W-9 case)
  const exp = new Date(opts.expirationDate + 'T00:00:00Z').getTime()
  const now = Date.now()
  if (exp < now) return 'expired'
  const days = (exp - now) / (24 * 60 * 60 * 1000)
  if (days <= 30) return 'expiring_soon'
  return 'valid'
}
